/* posthog-store.js — Da Vinci In You, Shopify theme V14 (staged for Phase 2, extended for Phase 5)
 * Created: 2026-09-02 14:58 ET · Phase 5 extension 2026-09-02 21:47 ET · cart confirmation rewrite 2026-09-02 23:20 ET
 * Learning_Posthog SPEC §3 · event names from EVENTS.md (P1 blanket rows + P2 rows)
 *
 * What this is: the blanket-designer analytics listeners, in a NEW asset, so the five SHA-pinned v3
 * files (product.coloring-blanket-v3.json, main-coloring-blanket-v3.liquid,
 * coloring-blanket-design-results-v3.liquid, coloring-blanket-v3.js, coloring-blanket-v3.css) stay
 * byte-identical. Everything below listens to signals v3 already emits (a CustomEvent, delegated
 * clicks/changes on data-coloring-* hooks, form submits) and confirms cart adds from GET /cart.js.
 *
 * Frozen event names (EVENTS.md): design_viewed, design_favorited, designs_paginated,
 * blanket_size_selected, blanket_added_to_cart, collection_variant_viewed.
 * Phase 5 (P2, EVENTS.md "Upsell collections and kits"): upsell_collection_viewed once per load of
 * /collections/free-coloring-pages-<designId>; kit_added_to_cart once per confirmed add of a product whose
 * cart-line handle contains "coloring-kit" (product form and quick-add modal).
 *
 * Cart adds (2026-09-02.3): window.fetch is NEVER wrapped any more. Shopify's web-pixels runtime, the
 * shop_events_listener and store apps all replace window.fetch too, and a wrapper chain is only as reliable as
 * every other wrapper in it (D1). Instead:
 *   1. intent — capture-phase "submit" on any form posting to /cart/add(.js) (v3 designer forms, Dawn product
 *      forms, the quick-add modal) and the designer's [data-coloring-add] click; the form fields are read and a
 *      GET /cart.js snapshot is started at that moment;
 *   2. confirmation — GET /cart.js again ~0.8 s, 2 s and 4 s later; success = a cart line for the posted variant
 *      whose quantity grew against the snapshot (or a new line). One event per intent, deduplicated per cart
 *      line key, properties read from the cart line (variant_id, product_id, sku, handle, _Design ID and so on);
 *   3. routing — an intent from the designer page emits blanket_added_to_cart only (never kit_added_to_cart,
 *      D2: the designer carts kit-handled products); any other form emits kit_added_to_cart only when the
 *      line's handle contains "coloring-kit", otherwise nothing.
 *
 * Rules honoured:
 *  - never calls posthog.init — layout/theme.liquid owns init, including the D10 engagement gate;
 *  - waits for posthog-js to be really loaded (window.posthog.__loaded === true) and queues until then;
 *  - never throws (every entry point is wrapped), never sends PII, no personal API key anywhere;
 *  - ES5 syntax only, no build step, no dependencies, never touches window.fetch;
 *  - loads on every page (theme.liquid) but binds designer listeners only on blanket templates and the
 *    cart-add intent listener only on pages that can add to cart (product/collection/search templates or a theme form).
 *
 * Debugging in a real Chrome window: run  window.__ph_store_debug = true  in DevTools, then every
 * queued/sent event is logged as "[posthog-store] sent design_viewed {properties}". __ph_store.pending()
 * shows how many events are waiting for posthog-js; __ph_store.loaded() whether it is ready;
 * __ph_store.cartWatch() how many add intents were opened / confirmed / polled.
 */
(function (window, document) {
  'use strict';

  var VERSION = '2026-09-11.1';

  // Frozen event names — EVENTS.md is the only place these are defined.
  var EV_DESIGN_VIEWED = 'design_viewed';
  var EV_DESIGN_FAVORITED = 'design_favorited';
  var EV_DESIGNS_PAGINATED = 'designs_paginated';
  var EV_SIZE_SELECTED = 'blanket_size_selected';
  var EV_ADDED_TO_CART = 'blanket_added_to_cart';
  var EV_COLLECTION_VARIANT = 'collection_variant_viewed';
  var EV_UPSELL_COLLECTION = 'upsell_collection_viewed';   // Phase 5 (P2)
  var EV_KIT_ADDED = 'kit_added_to_cart';                  // Phase 5 (P2)

  // Tunables.
  var QUEUE_MAX = 50;                    // events held while posthog-js is not loaded yet (memory only)
  var POLL_INTERVAL_MS = 250;            // re-check posthog.__loaded this often while events are waiting
  var POLL_MAX_MS = 60000;               // give up polling after this; the next capture or interaction restarts it
  var CLICK_LINK_WINDOW_MS = 1500;       // a design-card click explains a design-change only within this window
  var CAPTURE_VARIANT_DESIGN_PICKS = true;   // Design-N swatch picks also count as design_viewed (see README)
  var COLLECTION_VIEW_REQUIRED = true;   // collection_variant_viewed fires only when ?view= is present
  var INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll']; // same list as the D10 gate
  // Phase 5 (P2). A kit is any product whose handle contains this token (12,548 live products, POSTHOG_FOR_AGENTS §12).
  var KIT_HANDLE_TOKEN = 'coloring-kit';
  // Per-design upsell collection: /collections/free-coloring-pages-<designId>; the handle suffix is the design id (a2408130742).
  var UPSELL_COLLECTION_RE = /^\/collections\/free-coloring-pages-([A-Za-z0-9]+)\/?$/;
  // Where a cart add can start: templates that render add-to-cart forms, or the theme's own form / quick-add elements
  // in the initial DOM (buy-buttons.liquid:27-53, card-product.liquid:340, 360-362, 411). Elsewhere nothing is bound.
  var CART_FORM_TEMPLATES = ['product', 'collection', 'search'];
  var CART_FORM_SELECTOR = 'product-form, [data-type="add-to-cart-form"], quick-add-modal, quick-add-bulk';
  // Cart-add confirmation (2026-09-02.3).
  var CART_POLL_DELAYS_MS = [800, 2000, 4000];   // GET /cart.js this long after the intent; stops at the first confirmation
  var INTENT_MERGE_MS = 1500;                    // the add-button click and the submit it triggers are one intent
  var INTENT_MAX = 20;                           // open intents kept in memory
  var DESIGNER_FORM_SELECTOR = '[data-coloring-form]';   // main-coloring-blanket-v3.liquid:183, 240, 299
  var DESIGNER_ADD_SELECTOR = '[data-coloring-add]';     // main-coloring-blanket-v3.liquid:190, 291, 312
  var CART_ADD_PATH = '/cart/add';                       // {% form 'product' %} action (Dawn and v3 alike)

  if (window.__ph_store && window.__ph_store.version) return; // loaded twice → never bind listeners twice

  var ctx = window.__ph_ctx && typeof window.__ph_ctx === 'object' ? window.__ph_ctx : {};
  var queue = [];
  var pollTimer = null;
  var pollStartedAt = 0;
  var seenDesigns = {};
  var designChangeCount = 0;
  var lastCardClick = null;
  var pendingFavorite = null;
  var pendingPage = null;
  var designerBound = false;
  var cartWatchBound = false;  // the submit intent listener is installed (designer pages + pages that can add to cart)
  var upsellCaptured = false;
  var intents = [];            // open cart-add intents, oldest first
  var intentSeq = 0;
  var accounted = {};          // cart line key → quantity already attributed to an emitted event (dedupe across intents)
  var cartStats = { opened: 0, confirmed: 0, polls: 0, snapshots: 0 };

  // ---------------------------------------------------------------- small helpers

  function noop() {}
  function isFn(value) { return typeof value === 'function'; }
  function now() { return Date.now(); }
  function hasOwn(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }

  function str(value) {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && isFinite(value)) return String(value);
    return '';
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  function firstString(list) {
    for (var i = 0; i < list.length; i++) {
      var s = str(list[i]);
      if (s) return s;
    }
    return '';
  }

  function attr(el, name) {
    try { return el && isFn(el.getAttribute) ? el.getAttribute(name) : null; } catch (e) { return null; }
  }

  function data(el, name) { return attr(el, 'data-' + name); }

  function qs(selector, root) {
    try { return (root || document).querySelector(selector); } catch (e) { return null; }
  }

  function qsa(selector, root) {
    var out = [];
    try {
      var list = (root || document).querySelectorAll(selector);
      for (var i = 0; i < list.length; i++) out.push(list[i]);
    } catch (e) { /* invalid selector or missing API */ }
    return out;
  }

  function matches(el, selector) {
    if (!el || el.nodeType !== 1) return false;
    var fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    if (!isFn(fn)) return false;
    try { return fn.call(el, selector); } catch (e) { return false; }
  }

  function closest(el, selector) {
    var node = el;
    var hops = 0;
    while (node && hops < 64) {
      if (node.nodeType === 1 && isFn(node.closest)) {
        try { return node.closest(selector); } catch (e) { return null; }
      }
      if (matches(node, selector)) return node;
      node = node.parentNode;
      hops += 1;
    }
    return null;
  }

  function indexIn(list, el) {
    for (var i = 0; i < list.length; i++) if (list[i] === el) return i;
    return -1;
  }

  function text(el) { return el && typeof el.textContent === 'string' ? el.textContent : ''; }

  function clean(props) {
    var out = {};
    for (var key in props) {
      if (!hasOwn(props, key)) continue;
      var value = props[key];
      if (value === undefined || value === null || value === '') continue;
      out[key] = value;
    }
    return out;
  }

  function debug(kind, name, props) {
    try {
      if (window.__ph_store_debug && window.console && isFn(window.console.info)) {
        window.console.info('[posthog-store] ' + kind + ' ' + name, props);
      }
    } catch (e) { /* console is optional */ }
  }

  // ---------------------------------------------------------------- posthog bridge

  // The snippet's stub (window.posthog = [] with queued method calls) has no __loaded; array.js sets
  // __loaded = true synchronously inside init, after which capture() is real. Read window.posthog fresh
  // every time — array.js replaces the stub object. The same flag gates oai-attribution-cart.js.
  function isLoaded() {
    try {
      var ph = window.posthog;
      return !!(ph && ph.__loaded === true && isFn(ph.capture));
    } catch (e) { return false; }
  }

  function send(name, props) {
    try {
      window.posthog.capture(name, props);
      debug('sent', name, props);
    } catch (e) { /* never break the page */ }
  }

  function flush() {
    while (queue.length && isLoaded()) {
      var item = queue.shift();
      send(item[0], item[1]);
    }
  }

  function stopPoll() {
    if (pollTimer === null) return;
    try { window.clearInterval(pollTimer); } catch (e) { /* ignore */ }
    pollTimer = null;
  }

  function pollTick() {
    try {
      if (isLoaded()) { stopPoll(); flush(); return; }
      if (now() - pollStartedAt > POLL_MAX_MS) stopPoll();
    } catch (e) { stopPoll(); }
  }

  function ensurePoll() {
    if (pollTimer !== null || !isFn(window.setInterval)) return;
    pollStartedAt = now();
    try { pollTimer = window.setInterval(pollTick, POLL_INTERVAL_MS); } catch (e) { pollTimer = null; }
  }

  function capture(name, props) {
    try {
      var payload = clean(props || {});
      if (isLoaded()) { flush(); send(name, payload); return; }
      if (queue.length >= QUEUE_MAX) queue.shift();
      queue.push([name, payload]);
      debug('queued', name, payload);
      ensurePoll();
    } catch (e) { /* never break the page */ }
  }

  // With the D10 gate, posthog.init runs on the first human interaction; the poll may have given up
  // by then (long idle before the first scroll). Re-arm it once on the same interaction events.
  var passiveOption = false;
  try {
    var probe = Object.defineProperty({}, 'passive', { get: function () { passiveOption = { passive: true }; return true; } });
    window.addEventListener('ph-store-probe', noop, probe);
    window.removeEventListener('ph-store-probe', noop, probe);
  } catch (e) { passiveOption = false; }

  function bindInteractionRestart() {
    if (!isFn(window.addEventListener)) return;
    var fired = false;
    var handler = function () {
      if (fired) return;
      fired = true;
      for (var i = 0; i < INTERACTION_EVENTS.length; i++) {
        try { window.removeEventListener(INTERACTION_EVENTS[i], handler, passiveOption); } catch (e) { /* ignore */ }
      }
      if (queue.length) ensurePoll();
    };
    for (var j = 0; j < INTERACTION_EVENTS.length; j++) {
      try { window.addEventListener(INTERACTION_EVENTS[j], handler, passiveOption); } catch (e) { /* ignore */ }
    }
  }

  // ---------------------------------------------------------------- page context

  function ctxString(key) { return typeof ctx[key] === 'string' ? ctx[key] : ''; }
  function templateSuffix() { return ctxString('shopify_template_suffix'); }
  function isBlanketSuffix() { return templateSuffix().indexOf('coloring-blanket') === 0; }

  // 'coloring-blanket' → v1, 'coloring-blanket-v3' → v3, 'coloring-blanket-v5-proof' → v5-proof.
  function templateVersion() {
    var suffix = templateSuffix();
    if (suffix.indexOf('coloring-blanket') !== 0) return 'unknown';
    var rest = suffix.slice('coloring-blanket'.length).replace(/^-/, '');
    return rest || 'v1';
  }

  function designerRoot() { return qs('[data-coloring-page]'); }      // main-coloring-blanket-v3.liquid:59
  function resultsRoot() { return qs('[data-coloring-results]'); }    // coloring-blanket-design-results-v3.liquid:4
  function isDesignerPage() { return isBlanketSuffix() || !!designerRoot(); }

  // coloring-blanket-v3.js:200 exposes the page controller as root.coloringBlanket.
  function pageInstance() {
    var root = designerRoot();
    return root && root.coloringBlanket && typeof root.coloringBlanket === 'object' ? root.coloringBlanket : null;
  }

  function openV6Modal() {
    if (templateSuffix() !== 'coloring-blanket-v6') return null;
    return qs('[data-coloring-modal][data-coloring-selection-mode][open]');
  }

  function currentSelectionMode() {
    var modal = openV6Modal();
    var modalMode = modal ? str(data(modal, 'coloring-selection-mode')) : '';
    if (modalMode) return modalMode;
    var page = pageInstance();
    return page && typeof page.designMode === 'string' ? page.designMode : '';
  }

  function currentVariantId() {
    var modal = openV6Modal();
    var modalInput = modal && qs('[data-coloring-variant-id]', modal);
    var modalId = modalInput ? str(modalInput.value) : '';
    if (modalId) return modalId;
    var page = pageInstance();
    var id = page && page.variant ? str(page.variant.id) : '';
    if (id) return id;
    var input = qs('[data-coloring-variant-id]');
    return input ? str(input.value) : '';
  }

  // The hidden properties[_Design ID] input is exactly what the cart will receive.
  function currentDesignId() {
    var modal = openV6Modal();
    var modalInput = modal && qs('[data-coloring-design-id]', modal);
    var modalValue = modalInput ? str(modalInput.value) : '';
    if (modalValue) return modalValue;
    var input = qs('[data-coloring-design-id]');
    if (input) {
      var value = str(input.value);
      if (value) return value;
    }
    var page = pageInstance();
    return page && page.design ? str(page.design.designCode) : '';
  }

  function currentPathname() { return str(window.location && window.location.pathname); }

  function pageStatusText() {
    return text(qs('[data-coloring-page-status]')).replace(/\s+/g, ' ').trim();  // "Page 2 of 140"
  }

  function parsePageStatus(value) {
    var s = String(value || '');
    var both = s.match(/(\d+)\s*(?:of|\/)\s*(\d+)/);
    if (both) return { page: toInt(both[1]), total_pages: toInt(both[2]) };
    var single = s.match(/(\d+)/);
    return { page: single ? toInt(single[1]) : null, total_pages: null };
  }

  function gridPageSize() {
    var results = resultsRoot();
    if (results) return toInt(data(results, 'page-size')) || 12;
    if (templateSuffix() !== 'coloring-blanket-v6') return 12;
    return toInt(data(qs('[data-coloring-design-grid]'), 'page-size')) || 12;
  } // v3 default is 12 too

  // Where a card/button sits: a shelf rail ([data-coloring-shelf]), one of the two v4 rails
  // ([data-coloring-recommendations] / [data-coloring-recently-viewed] — results-v4.liquid:120-151, filled by
  // coloring-blanket-v4.js createCard(record, { scope }); both absent on v3) or the paginated grid
  // ([data-coloring-design-grid]). Rail positions are 1-based inside the rail; grid positions are absolute:
  // (page - 1) * pageSize + index.
  function describePlacement(node) {
    var card = closest(node, '[data-coloring-design-card]');
    var shelf = closest(node, '[data-coloring-shelf]');
    if (shelf) {
      var shelfIndex = card ? indexIn(qsa('[data-coloring-design-card]', shelf), card) : -1;
      return { origin: 'shelf', shelf_id: str(data(shelf, 'coloring-shelf')), position: shelfIndex >= 0 ? shelfIndex + 1 : null };
    }
    var rail = closest(node, '[data-coloring-recommendations]');           // v4 rail (results-v4.liquid:126); never matches on v3
    var railOrigin = rail ? 'recommendations' : 'recently_viewed';
    if (!rail) rail = closest(node, '[data-coloring-recently-viewed]');    // v4 rail (results-v4.liquid:143)
    if (rail) {
      var railIndex = card ? indexIn(qsa('[data-coloring-design-card]', rail), card) : -1;
      return { origin: railOrigin, position: railIndex >= 0 ? railIndex + 1 : null };
    }
    var grid = closest(node, '[data-coloring-design-grid]');
    if (grid) {
      var gridIndex = card ? indexIn(qsa('[data-coloring-design-card]', grid), card) : -1;
      var page = parsePageStatus(pageStatusText()).page || 1;
      return { origin: 'grid', position: gridIndex >= 0 ? (page - 1) * gridPageSize() + gridIndex + 1 : null, page: page };
    }
    return { origin: 'other', position: null };
  }

  // ---------------------------------------------------------------- design_viewed

  // coloring-blanket-v3.js:592-594 — selectDesign() dispatches
  // CustomEvent('coloring:design-change', { bubbles: true, detail: { design } }) on the page root.
  // detail.design = { title, designCode, handle, id, image, imageAlt, coverImage, coverImageAlt, sourceVariants }.
  function onDesignChange(event) {
    try {
      var design = event && event.detail && event.detail.design;
      if (!design || typeof design !== 'object') return;
      designChangeCount += 1;
      var id = firstString([design.designCode, design.code, design.id, design.handle]);
      if (!id) return;
      var key = id.toUpperCase();
      if (seenDesigns[key]) return;                     // one design_viewed per design per page
      seenDesigns[key] = true;
      var placement = explainDesignChange(id);
      capture(EV_DESIGN_VIEWED, {
        design_id: id,
        design_source: firstString([design.handle, design.source]),  // legacy product handle = properties[_Design Source]
        template_version: templateVersion(),
        position: placement.position,
        origin: placement.origin,
        shelf_id: placement.shelf_id,
        page: placement.page,
        selection_mode: currentSelectionMode() || 'legacy',
        design_product_id: str(design.id)                 // legacy product id → joins order_paid.product_ids
      });
    } catch (e) { /* never break the page */ }
  }

  // selectDesign() has exactly two callers: the card click (v3.js:1484-1488) and the #design= /
  // ?design= deep link resolved after the catalog loads (v3.js:1277-1285).
  function explainDesignChange(id) {
    var click = lastCardClick;
    if (click && click.code && click.code.toUpperCase() === id.toUpperCase() && now() - click.at <= CLICK_LINK_WINDOW_MS) {
      lastCardClick = null;
      return click.placement;
    }
    var url = str(window.location && (String(window.location.hash || '') + ' ' + String(window.location.search || '')));
    if (designChangeCount === 1 && /(?:^|[#?&\s])design=/.test(url)) return { origin: 'deep_link', position: null };
    return { origin: 'other', position: null };
  }

  // ---------------------------------------------------------------- clicks (cards, favorites, pager, add button)

  // Capture phase runs before v3's own element-level handlers: remember the "before" state.
  function onClickCapture(event) {
    try {
      var target = event && event.target;
      var card = closest(target, '[data-coloring-design-card]');            // v3.js:1440-1449
      if (card) {
        lastCardClick = { code: str(data(card, 'design-code')), at: now(), placement: describePlacement(card) };
        return;
      }
      var favorite = closest(target, '[data-coloring-favorite]');           // v3.js:1490-1505
      if (favorite) {
        pendingFavorite = { button: favorite, wasPressed: attr(favorite, 'aria-pressed') === 'true', at: now() };
        return;
      }
      var pager = closest(target, '[data-coloring-page-previous], [data-coloring-page-next]'); // results-v3.liquid:128-130
      if (pager) { pendingPage = { button: pager, before: pageStatusText(), at: now() }; return; }
      var add = closest(target, DESIGNER_ADD_SELECTOR);                     // v3.js:877-887 submits the surrounding form
      if (add) {
        var form = add.form && add.form.nodeType === 1 ? add.form : closest(add, 'form');
        if (form) openIntent(form, 'click');
      }
    } catch (e) { /* never break the page */ }
  }

  // Bubble phase at document runs after v3's handlers (toggleFavorite / goToPage → render).
  function onClickBubble(event) {
    try {
      var target = event && event.target;
      var favorite = closest(target, '[data-coloring-favorite]');
      if (favorite) { finishFavorite(favorite); return; }
      var pager = closest(target, '[data-coloring-page-previous], [data-coloring-page-next]');
      if (pager) finishPagination(pager);
    } catch (e) { /* never break the page */ }
  }

  // toggleFavorite (v3.js:1016-1043) flips the code unconditionally, so favorited = !aria-pressed(before).
  function finishFavorite(button) {
    var pending = pendingFavorite;
    pendingFavorite = null;
    if (!pending || pending.button !== button) return;
    var code = str(data(button, 'design-code'));
    if (!code) return;
    var placement = describePlacement(button);
    capture(EV_DESIGN_FAVORITED, {
      design_id: code.toUpperCase(),                    // selectDesign() upper-cases codes; keep ids joinable
      favorited: !pending.wasPressed,
      origin: placement.origin,
      shelf_id: placement.shelf_id,
      template_version: templateVersion()
    });
  }

  // goToPage (v3.js:1335-1341) → render() rewrites "Page X of Y" synchronously (v3.js:1431).
  // No change in that text = no page change (not loaded, or clamped) = no event.
  function finishPagination(button) {
    var pending = pendingPage;
    pendingPage = null;
    if (!pending || pending.button !== button) return;
    var after = pageStatusText();
    if (!after || after === pending.before) return;
    var parsed = parsePageStatus(after);
    capture(EV_DESIGNS_PAGINATED, {
      page: parsed.page,
      total_pages: parsed.total_pages,
      direction: closest(button, '[data-coloring-page-next]') ? 'next' : 'previous',
      template_version: templateVersion()
    });
  }

  // ---------------------------------------------------------------- size / Design option changes

  // v3 binds 'change' on the page root (v3.js:241-305) and unchecks a rejected pick (sold out).
  // At document level we run after it: input.checked === true means the pick was accepted.
  function onChange(event) {
    try {
      var input = closest(event && event.target, '[data-coloring-option-input]');
      if (!input || !input.checked) return;
      var position = toInt(data(input, 'option-position'));
      var surface = closest(input, '[data-coloring-options-surface]');
      var surfaceName = str(data(surface, 'coloring-options-surface'));   // 'hero' | 'modal'
      if (position === 1) {                                              // option 1 = blanket size
        var modal = openV6Modal();
        var modalSize = modal && closest(input, '[data-coloring-modal]') === modal
          ? str(data(input, 'coloring-option-label'))
          : '';
        capture(EV_SIZE_SELECTED, {
          variant_id: currentVariantId(),
          size: modalSize || str(input.value),
          selection_mode: currentSelectionMode(),
          design_id: currentDesignId(),
          ui_surface: surfaceName,
          template_version: templateVersion()
        });
        return;
      }
      if (position === 2 && CAPTURE_VARIANT_DESIGN_PICKS) {              // option 2 = variant-backed "Design N"
        var option = str(input.value);
        var sku = currentDesignId();                                     // applyVariantDesign writes variant.sku there (v3.js:655-657)
        var id = sku || (option ? 'design-' + option : '');
        if (!id || seenDesigns[id]) return;
        seenDesigns[id] = true;
        var siblings = surface ? qsa('[data-coloring-option-input][data-option-position="2"]', surface) : [];
        var index = indexIn(siblings, input);
        capture(EV_DESIGN_VIEWED, {
          design_id: id,
          design_source: 'coloring-blanket',                             // what properties[_Design Source] holds in variant mode (v3.js:658-660)
          template_version: templateVersion(),
          position: index >= 0 ? index + 1 : null,
          origin: 'variant_option',
          selection_mode: 'variant',
          design_option: option,
          ui_surface: surfaceName
        });
      }
    } catch (e) { /* never break the page */ }
  }

  // ---------------------------------------------------------------- cart adds: intent + /cart.js confirmation

  // Shopify's Ajax cart (GET /cart.js) answers { item_count, items: [ { key, id, variant_id, product_id, sku,
  // handle, quantity, properties, title and more } ] }. Lines are keyed by "key" ("<variant_id>:<hash of properties>"),
  // so the same blanket variant with two different _Design ID values is two lines. Never read here: title,
  // product_title, url, image, price fields, and only the five underscore properties below are ever looked at.
  function cartJsUrl() {
    var routes = window.routes && typeof window.routes === 'object' ? str(window.routes.cart_url) : '';   // theme.liquid: window.routes.cart_url
    var configured = str(data(designerRoot(), 'cart-url'));                                                 // main-coloring-blanket-v3.liquid:62
    var base = (configured || routes || '/cart').split('?')[0].split('#')[0].replace(/\/+$/, '');
    if (/\.js$/.test(base)) return base;
    return (base || '/cart') + '.js';
  }

  function normalizeCart(json) {
    if (!json || typeof json !== 'object' || !json.items || typeof json.items.length !== 'number') return null;
    var lines = {};
    for (var i = 0; i < json.items.length; i++) {
      var item = json.items[i];
      if (!item || typeof item !== 'object') continue;
      var variantId = str(item.variant_id) || str(item.id);
      var key = str(item.key) || (variantId + ':' + i);
      lines[key] = {
        key: key,
        variant_id: variantId,
        product_id: str(item.product_id),
        sku: str(item.sku),
        handle: str(item.handle),
        quantity: toInt(item.quantity) || 0,
        properties: item.properties && typeof item.properties === 'object' ? item.properties : {}
      };
    }
    return { item_count: toInt(json.item_count), lines: lines };
  }

  // callback(cart | null) exactly once; whatever window.fetch is at call time is fine (every wrapper passes a GET through).
  function fetchCart(callback) {
    var done = false;
    var finish = function (cart) {
      if (done) return;
      done = true;
      try { callback(cart); } catch (e) { /* never break the page */ }
    };
    try {
      if (!isFn(window.fetch)) { finish(null); return; }
      var result = window.fetch(cartJsUrl(), { method: 'GET', headers: { Accept: 'application/json' } });
      if (!result || !isFn(result.then)) { finish(null); return; }
      result.then(function (response) {
        try {
          if (!response || response.ok !== true || !isFn(response.json)) { finish(null); return; }
          var parsed = response.json();
          if (parsed && isFn(parsed.then)) parsed.then(function (json) { finish(normalizeCart(json)); }, function () { finish(null); });
          else finish(normalizeCart(parsed));
        } catch (e) { finish(null); }
      }, function () { finish(null); });
    } catch (e) { finish(null); }
  }

  function lineProp(line, name) {
    var value = line && line.properties ? line.properties[name] : undefined;
    return typeof value === 'string' || typeof value === 'number' ? str(value) : '';
  }

  // What the form is about to post: the variant, the quantity (Dawn's quantity input can sit outside the form and
  // point at it with form="<id>", main-product.liquid:289) and v3's hidden design properties.
  function readForm(form) {
    var out = { variant_id: '', quantity: '', design_id: '', design_source: '', origin_page: '', selection_mode: '', origin_variant_id: '' };
    if (!form) return out;
    var controls = qsa('input, select, textarea', form);
    var formId = str(attr(form, 'id'));
    if (formId) controls = controls.concat(qsa('[form="' + formId + '"]'));
    for (var i = 0; i < controls.length; i++) {
      var name = str(attr(controls[i], 'name'));
      if (!name) continue;
      var value = str(controls[i].value);
      if (name === 'id') { if (!out.variant_id) out.variant_id = value; }
      else if (name === 'quantity') { if (value) out.quantity = value; }
      else if (name === 'properties[_Design ID]') out.design_id = value;
      else if (name === 'properties[_Design Source]') out.design_source = value;
      else if (name === 'properties[_Origin Page]') out.origin_page = value;
      else if (name === 'properties[_Selection Mode]') out.selection_mode = value;
      else if (name === 'properties[_Origin Variant]') out.origin_variant_id = value;
    }
    return out;
  }

  function isCartAddForm(form) {
    if (!form || form.nodeType !== 1 || String(form.tagName || '').toUpperCase() !== 'FORM') return false;
    if (matches(form, DESIGNER_FORM_SELECTOR)) return true;
    var action = str(attr(form, 'action'));
    return action.indexOf(CART_ADD_PATH) !== -1;
  }

  // Designer forms carry data-coloring-form and sit under [data-coloring-page]; on a blanket template every
  // add is a blanket add (v3 renders no other form). Everything else is judged by the cart line's handle.
  function intentKind(form) {
    if (matches(form, DESIGNER_FORM_SELECTOR) || closest(form, '[data-coloring-page]') || isDesignerPage()) return 'blanket';
    return 'kit';
  }

  // The snapshot (or a later poll) says the cart is smaller than what we already attributed: forget the surplus.
  function reconcileAccounted(cart) {
    for (var key in accounted) {
      if (!hasOwn(accounted, key)) continue;
      var line = cart.lines[key];
      if (!line) delete accounted[key];
      else if (accounted[key] > line.quantity) accounted[key] = line.quantity;
    }
  }

  function clearIntentTimer(intent) {
    if (intent.timer === null) return;
    try { window.clearTimeout(intent.timer); } catch (e) { /* ignore */ }
    intent.timer = null;
  }

  function closeIntent(intent) {
    intent.done = true;
    clearIntentTimer(intent);
    for (var i = intents.length - 1; i >= 0; i--) if (intents[i] === intent) intents.splice(i, 1);
  }

  function openIntent(form, source) {
    try {
      if (!isCartAddForm(form)) return null;
      var t = now();
      for (var i = 0; i < intents.length; i++) {
        var open = intents[i];
        if (open.form === form && !open.done && t - open.at <= INTENT_MERGE_MS) {
          if (!open.fields.variant_id) open.fields = readForm(form);   // the click read an incomplete form; the submit sees the final one
          return open;
        }
      }
      var intent = { id: ++intentSeq, at: t, form: form, source: source, kind: intentKind(form), fields: readForm(form), snapshot: null, snapshotDone: false, polls: 0, done: false, timer: null };
      intents.push(intent);
      while (intents.length > INTENT_MAX) closeIntent(intents[0]);
      cartStats.opened += 1;
      fetchCart(function (cart) {                                          // pre-add snapshot, started before the theme's POST
        intent.snapshotDone = true;
        intent.snapshot = cart;
        if (cart) { cartStats.snapshots += 1; reconcileAccounted(cart); }
      });
      schedulePoll(intent, 0);
      return intent;
    } catch (e) { return null; }
  }

  function schedulePoll(intent, index) {
    if (intent.done || index >= CART_POLL_DELAYS_MS.length) { if (!intent.done && index >= CART_POLL_DELAYS_MS.length) closeIntent(intent); return; }
    if (!isFn(window.setTimeout)) { closeIntent(intent); return; }
    var delay = CART_POLL_DELAYS_MS[index] - (now() - intent.at);
    if (delay < 0) delay = 0;
    try {
      intent.timer = window.setTimeout(function () { pollIntent(intent, index); }, delay);
    } catch (e) { closeIntent(intent); }
  }

  function pollIntent(intent, index) {
    try {
      intent.timer = null;
      if (intent.done) return;
      intent.polls += 1;
      cartStats.polls += 1;
      var last = index >= CART_POLL_DELAYS_MS.length - 1;
      fetchCart(function (cart) {
        try {
          if (intent.done) return;
          // No baseline yet (the snapshot request is still in flight): wait for the next poll unless this is the last one.
          if (cart && (intent.snapshotDone || last) && confirmIntent(intent, cart)) return;
          if (last) { closeIntent(intent); return; }
          schedulePoll(intent, index + 1);
        } catch (e) { closeIntent(intent); }
      });
    } catch (e) { closeIntent(intent); }
  }

  function baselineFor(intent, key) {
    var snap = intent.snapshot && intent.snapshot.lines[key] ? intent.snapshot.lines[key].quantity : 0;
    var acc = hasOwn(accounted, key) ? accounted[key] : 0;
    return snap > acc ? snap : acc;
  }

  // The cart line this intent produced: the posted variant (when known) whose quantity grew past the baseline.
  // Several candidates (same variant, different properties): prefer the one carrying the posted _Design ID.
  function pickLine(intent, cart) {
    var want = intent.fields.variant_id;
    var wantDesign = intent.fields.design_id.toUpperCase();
    var best = null;
    for (var key in cart.lines) {
      if (!hasOwn(cart.lines, key)) continue;
      var line = cart.lines[key];
      if (want && line.variant_id !== want) continue;
      var delta = line.quantity - baselineFor(intent, key);
      if (delta <= 0) continue;
      var score = wantDesign && lineProp(line, '_Design ID').toUpperCase() === wantDesign ? 2 : 1;
      if (!best || score > best.score || (score === best.score && delta > best.delta)) best = { line: line, delta: delta, score: score };
    }
    return best;
  }

  function confirmIntent(intent, cart) {
    var found = pickLine(intent, cart);
    if (!found) return false;
    closeIntent(intent);
    var posted = toInt(intent.fields.quantity);
    // With a snapshot the delta is exact, capped at the posted quantity so two quick adds of one line (hero then
    // sticky form) count once each; without a snapshot (the GET failed) the posted quantity is the best available.
    var quantity = intent.snapshot ? found.delta : (posted || 1);
    if (intent.snapshot && posted && posted > 0 && posted < quantity) quantity = posted;
    accounted[found.line.key] = found.line.quantity - found.delta + quantity;   // baseline + what this intent claimed
    cartStats.confirmed += 1;
    if (intent.kind === 'blanket') emitBlanketAdd(intent, found.line, quantity);
    else emitKitAdd(intent, found.line, quantity);
    return true;
  }

  // ---------------------------------------------------------------- blanket_added_to_cart

  // Designer forms (main-coloring-blanket-v3.liquid:183-198, 240-297, 299-317) post id, quantity and the hidden
  // properties; v3's addToCart (v3.js:890-927) adds _Origin Page, _Origin Variant and _Selection Mode at submit
  // time, so the cart line is the authoritative source and the form fields are the fallback.
  function emitBlanketAdd(intent, line, quantity) {
    var f = intent.fields;
    capture(EV_ADDED_TO_CART, {
      variant_id: line.variant_id || f.variant_id,
      design_id: lineProp(line, '_Design ID') || f.design_id,
      design_source: lineProp(line, '_Design Source') || f.design_source,
      origin_page: lineProp(line, '_Origin Page') || f.origin_page || currentPathname(),
      origin_variant_id: lineProp(line, '_Origin Variant') || f.origin_variant_id,
      selection_mode: lineProp(line, '_Selection Mode') || f.selection_mode,
      quantity: quantity,
      template_version: templateVersion()
    });
  }

  // ---------------------------------------------------------------- Phase 5 (P2): kit_added_to_cart

  function isKitHandle(handle) {
    return str(handle).toLowerCase().indexOf(KIT_HANDLE_TOKEN) !== -1;
  }

  // /collections/<handle>[/products/<handle>] → the collection the shopper is browsing inside (card-product.liquid:138
  // links to card_product.url, which keeps the collection prefix). On an upsell collection the handle suffix is the
  // design id; upper-cased so it joins design_viewed.design_id and the cart's _Design ID.
  function describeCollectionPath(pathname) {
    var m = String(pathname || '').match(/^\/collections\/([^\/?#]+)/);
    var handle = m ? m[1] : '';
    var upsell = handle.match(/^free-coloring-pages-([A-Za-z0-9]+)$/);
    return { collection_handle: handle, design_id: upsell ? upsell[1].toUpperCase() : '' };
  }

  // Product form and quick-add modal (both product-form.js:45). The cart line decides: no "coloring-kit" in the
  // handle (a regular product, a free coloring page, one of the 54 custom "coloring-page-kit" products) → nothing.
  function emitKitAdd(intent, line, quantity) {
    if (!isKitHandle(line.handle)) return;
    var pathname = currentPathname();
    var where = describeCollectionPath(pathname);
    capture(EV_KIT_ADDED, {
      product_handle: line.handle,
      variant_id: line.variant_id || intent.fields.variant_id,
      product_id: line.product_id,                                        // joins order_paid.product_ids
      sku: line.sku,                                                      // joins order_paid.skus
      quantity: quantity,
      design_id: where.design_id || intent.fields.design_id,
      collection_handle: where.collection_handle,
      origin_page: pathname,
      handle_source: 'cart'                                               // read from the confirmed /cart.js line
    });
  }

  // ---------------------------------------------------------------- collection_variant_viewed

  function readQueryParam(name) {
    var search = str(window.location && window.location.search);
    if (!search) return '';
    try {
      if (isFn(window.URLSearchParams)) {
        var value = new window.URLSearchParams(search).get(name);
        return typeof value === 'string' ? value.trim() : '';
      }
    } catch (e) { /* fall through */ }
    var pairs = search.replace(/^\?/, '').split('&');
    for (var i = 0; i < pairs.length; i++) {
      var kv = pairs[i].split('=');
      try {
        if (decodeURIComponent(kv[0]) === name) return decodeURIComponent((kv[1] || '').replace(/\+/g, ' ')).trim();
      } catch (e) { /* malformed pair */ }
    }
    return '';
  }

  function captureCollectionVariant() {
    var template = ctxString('shopify_template');
    var pathname = str(window.location && window.location.pathname);
    var isCollection = template === 'collection' || (!template && pathname.indexOf('/collections/') !== -1);
    if (!isCollection) return;
    var view = readQueryParam('view');
    if (view && !/^[A-Za-z0-9_-]{1,80}$/.test(view)) view = '';           // template suffix shape only; no junk cardinality
    if (COLLECTION_VIEW_REQUIRED && !view) return;
    var handle = (pathname.match(/\/collections\/([^\/?#]+)/) || [])[1] || '';
    capture(EV_COLLECTION_VARIANT, { view: view, collection_handle: handle });
  }

  // ---------------------------------------------------------------- Phase 5 (P2): upsell_collection_viewed

  // Distinct product handles in the rendered collection grid: main-collection-product-grid.liquid:173-210 renders
  // ul#product-grid with one card-product per li; card-product.liquid:138 and :176 both link to card_product.url
  // (/collections/<collection>/products/<handle>), so the two links per card count once. null = no grid on the page.
  function gridProductHandles() {
    var grid = qs('#product-grid') || qs('.product-grid');
    if (!grid) return null;
    var seen = {};
    var handles = [];
    var links = qsa('a[href]', grid);
    for (var i = 0; i < links.length; i++) {
      var m = str(attr(links[i], 'href')).match(/\/products\/([^\/?#]+)/);
      if (!m || seen[m[1]]) continue;
      seen[m[1]] = true;
      handles.push(m[1]);
    }
    return handles;
  }

  function captureUpsellCollection() {
    if (upsellCaptured) return;
    var template = ctxString('shopify_template');
    if (template && template !== 'collection') return;                  // the pathname alone decides only without __ph_ctx
    var m = currentPathname().match(UPSELL_COLLECTION_RE);
    if (!m) return;
    upsellCaptured = true;
    var handles = gridProductHandles();
    var kits = 0;
    if (handles) {
      for (var i = 0; i < handles.length; i++) if (isKitHandle(handles[i])) kits += 1;
    }
    var page = toInt(readQueryParam('page'));
    capture(EV_UPSELL_COLLECTION, {
      design_id: m[1].toUpperCase(),                                      // same case as design_viewed.design_id / cart _Design ID
      kit_count: handles ? kits : null,                                   // absent when the grid markup is missing (unknown, not 0)
      grid_product_count: handles ? handles.length : null,
      collection_handle: 'free-coloring-pages-' + m[1],
      page: page && page > 1 ? page : 1
    });
  }

  // ---------------------------------------------------------------- boot

  // Capture phase at document runs before v3's form-level submit handler (v3.js:878-887) and before
  // product-form.js's (product-form.js:9), i.e. before either one calls fetch: the snapshot request goes out first.
  function onSubmitCapture(event) {
    try {
      var form = event && event.target;
      if (!form || !isCartAddForm(form)) return;
      openIntent(form, 'submit');
    } catch (e) { /* never break the page */ }
  }

  function bindDesigner() {
    if (designerBound || !isFn(document.addEventListener)) return;
    designerBound = true;
    document.addEventListener('coloring:design-change', onDesignChange, false);
    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('click', onClickBubble, false);
    document.addEventListener('change', onChange, false);
  }

  function canAddToCart() {
    if (indexIn(CART_FORM_TEMPLATES, ctxString('shopify_template')) !== -1) return true;
    return !!qs(CART_FORM_SELECTOR);
  }

  function bindCartWatch() {
    if (cartWatchBound || !isFn(document.addEventListener)) return;
    if (!isDesignerPage() && !canAddToCart()) return;
    cartWatchBound = true;
    document.addEventListener('submit', onSubmitCapture, true);
  }

  function boot() {
    try { bindInteractionRestart(); } catch (e) { /* ignore */ }
    try { captureCollectionVariant(); } catch (e) { /* ignore */ }
    try { captureUpsellCollection(); } catch (e) { /* ignore */ }
    try { if (isDesignerPage()) bindDesigner(); } catch (e) { /* ignore */ }
    try { bindCartWatch(); } catch (e) { /* ignore */ }
  }

  window.__ph_store = {
    version: VERSION,
    loaded: isLoaded,
    pending: function () { return queue.length; },
    // blanket = designer listeners bound (designer adds route to blanket_added_to_cart); kit = cart-add intents watched
    taps: function () { return { blanket: designerBound, kit: cartWatchBound }; },
    cartWatch: function () { return { opened: cartStats.opened, confirmed: cartStats.confirmed, polls: cartStats.polls, snapshots: cartStats.snapshots, open: intents.length }; }
  };

  try {
    if (document.readyState === 'loading' && isFn(document.addEventListener)) {
      document.addEventListener('DOMContentLoaded', function () { try { boot(); } catch (e) { /* ignore */ } }, false);
    } else {
      boot();
    }
  } catch (e) { /* never break the page */ }
})(window, document);
